import { useMutation, useQueryClient, MutationOptions } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import { deleteList } from 'Services';
import { RQ_ALL_LIST } from 'Utils';
import { useTranslation } from 'react-i18next';
import useAlertBanner from 'Hooks/reduxHelperHooks/useAlertBanner';

import useReduxModal from 'Hooks/reduxHelperHooks/useReduxModal';
import { useTemplateStore } from 'Hooks/zustand/useTemplateStore';
import { getTranslationErrorHandling } from '../helper';

const useDeleteList = ({
    mutationOptions: { onSuccess, onError },
    listName
}: {
    mutationOptions: Omit<MutationOptions<AxiosResponse<PatchList>, any, any, any>, 'mutationKey'>;
    listName?: string;
}) => {
    const queryClient = useQueryClient();
    const { t } = useTranslation();
    const { templateRepositoryId } = useTemplateStore.getState().template;
    const { addAlert } = useAlertBanner();

    const { addTechnicalErrorModal } = useReduxModal();
    const errorStringKey = getTranslationErrorHandling(t);

    const handleNotFound = () => {
        addTechnicalErrorModal('', errorStringKey('listDeleted'), () => {}, {
            status: 'warning',
            content: errorStringKey('listDeletedAlready')
        });
    };
    const handleLinkedList = () => {
        addTechnicalErrorModal('', errorStringKey('listLinked'), () => {}, {
            status: 'warning',
            content: errorStringKey('listLinkedAlready')
        });
    };

    return useMutation((listId: number) => deleteList(listId, { templateRepositoryId: templateRepositoryId! }), {
        onSuccess: (res: any, variable, context) => {
            if (onSuccess) {
                onSuccess(res, variable, context);
            }
            addAlert({
                id: `list-updated-${Date.now()}`,
                content: t('listDeletedSuccessful', { listName }),
                status: 'success'
            });
        },
        onError: (err: any, variable, context) => {
            switch (err?.status) {
                case 404: {
                    handleNotFound();
                    break;
                }
                case 409: {
                    handleLinkedList();
                    break;
                }
                default:
                    addTechnicalErrorModal();
                    break;
            }

            if (onError) {
                onError(err, variable, context);
            }
        },
        onSettled: () => {
            queryClient.invalidateQueries([RQ_ALL_LIST]);
        }
    });
};

export default useDeleteList;
